/**
 * Per-apartment go-live checklist model + progress math (COL-529).
 *
 * The wizard's Step 3 tracks 15 proofs per apartment, grouped into Documents (7),
 * The unit (6) and Contacts (2). This module holds the state shape, the defaults,
 * and the single pure function that counts how many of the 15 are done — ported
 * verbatim from the approved design comp so the UI and the x/15 tally can never
 * disagree. ChecklistV3 renders it; the progress helper is unit-tested on its own.
 *
 * Storage maps to `apartment_checklist_items` (status/source/note, one row per
 * item_key) + `apartment_checklist_files` + `apartments.doorbell_name`. Structured
 * extras that have no column (network name, broadcasting number, key-handover mode,
 * contact people) fold into the item's `note`. See the submit hooks for the write.
 */

/** The 7 document proofs, in display order. `item_key` values in the DB table. */
export const DOC_IDS = [
  "wlan",
  "strom",
  "gas",
  "rundfunk",
  "energie",
  "eigentum",
  "grundriss",
] as const;
export type DocId = (typeof DOC_IDS)[number];

/**
 * All 15 checklist item keys, in DB + display order (docs 7, unit 6, contacts
 * 2). These are the `item_key` values of apartment_checklist_items; the CHECK
 * constraint on that table is the authority — keep the two in sync.
 */
export const CHECKLIST_ITEM_KEYS = [
  ...DOC_IDS,
  "photos",
  "ware",
  "washer",
  "smoke",
  "key",
  "bell",
  "contact",
  "viewing",
] as const;
export type ChecklistItemKey = (typeof CHECKLIST_ITEM_KEYS)[number];

/** Persisted status of a checklist proof — the apartment_checklist_items values. */
export type ChecklistItemStatus =
  | "open"
  | "requested"
  | "fulfilled"
  | "not_applicable";

/** One apartment_checklist_items row as the submit hooks write it. */
export type ChecklistItemRow = {
  item_key: ChecklistItemKey;
  status: ChecklistItemStatus;
  /** Who supplies the proof; NULL for the unit/contact items. */
  source: DocSource;
  note: string | null;
};

/**
 * Photos are collected per "shot key": the building exterior (2 photos), one key
 * per bedroom (r1..rN, 4 corner photos each) and the common area (4 photos).
 * CAP is how many photo slots a shot key has.
 */
export const photoCap = (shotKey: string): number =>
  shotKey === "outside" ? 2 : 4;

/** Shot keys for a unit with `bedrooms` bedrooms: exterior, each bedroom, common. */
export function shotKeys(bedrooms: number): string[] {
  const rooms = Array.from(
    { length: Math.max(0, bedrooms) },
    (_, i) => `r${i + 1}`
  );
  return ["outside", ...rooms, "common"];
}

/**
 * Bedrooms drive the photo-slot count and the key proof. `numRooms` is the
 * wizard's form string. The single source for all four call sites (Step 3
 * display, step validation, and both submit paths), so the photos proof is
 * scored on submit exactly as the wizard scored it. An empty field means zero
 * bedrooms — never a silent fallback to apt.rooms.length, which scored submit
 * differently from what the owner completed.
 */
export const bedroomsOf = (numRooms: string): number =>
  Math.max(0, parseInt(numRooms, 10) || 0);

/** A document proof's status. The DB stores "fulfilled" for "done"; the UI and
 *  this model use "done" (the mapping lives in the submit hooks). */
export type DocStatus = "open" | "requested" | "done";
export type DocSource = "owner" | "coliviq" | null;
/** Rundfunk (broadcasting fee) sub-state: has a number, applied, or not yet. */
export type RundfunkMode = "number" | "applied" | "notyet" | null;

export type DocState = {
  status: DocStatus;
  /** Who supplied/was asked for the proof — drives the source chip. */
  src: DocSource;
  /** Owner's free-text note. Display/recordkeeping only — never promotes the
   *  item to done (done-ness is structural; see checklistItemDone). */
  note: string | null;
  /** Gas only: marked "not applicable" (no gas in the building). */
  na?: boolean;
  /** Rundfunk only. */
  rb?: RundfunkMode;
  /** Public URL of an uploaded proof file. Folded into the note on save as
   *  `Datei: <url>` and parsed back on edit — no separate persistence column. */
  fileUrl?: string;
};

export type Person = { name: string; phone: string; email: string };
/** Smoke-detector answer. "no" hard-blocks go-live (legal requirement). */
export type SmokeAnswer = "yes" | "no" | null;
/** Key handover: via a KeyNest shop, or a named person. */
export type KeyMode = "keynest" | "person" | null;

/** Per-apartment checklist state. Mirrors the design comp's grouped shape. */
export type ChecklistState = {
  docs: Record<DocId, DocState>;
  /** Rundfunk contribution number (when docs.rundfunk.rb === "number"). */
  rbNumber: string;
  smoke: SmokeAnswer;
  key: KeyMode;
  wlanUser: string;
  /**
   * WLAN password. Held in wizard memory only — NEVER written to an unencrypted
   * column. The submit hooks persist the network name + a "password provided"
   * marker, not this value. See the PR notes; at-rest encryption is deferred.
   */
  wlanPass: string;
  /**
   * True when a WLAN password was provided at least once. Survives the
   * round-trip (the plaintext never does), so the "Passwort hinterlegt" marker
   * isn't silently dropped on resubmit. Never holds the password itself.
   */
  wlanPassProvided: boolean;
  people: { hv: Person; hm: Person; key: Person; view: Person };
  viewing: { allowed: boolean | null };
  /** Uploaded photo count per shot key (display only; `pics` drives progress). */
  shots: Record<string, number>;
  /** Which photo slots are filled, keyed "<shotKey>:<index>". Drives done-ness. */
  pics: Record<string, boolean>;
  /** Storage path of the uploaded photo per filled slot, same "<shotKey>:<index>"
   *  keys as `pics`. Empty for a slot filled before real uploads existed; a real
   *  upload sets both. Persisted to apartment_checklist_files on submit. */
  picPaths: Record<string, string>;
  /** Which shot keys have a 360° video. */
  videos: Record<string, boolean>;
  equip: {
    /** `photo` is the done-making flag; `photoPath` is the uploaded file's
     *  storage path (empty when none / restored from a round-trip, same
     *  ceiling as picPaths). Persisted to apartment_checklist_files on submit. */
    ware: { ok: boolean | null; photo: boolean; photoPath: string };
    washer: { ok: boolean | null; photo: boolean; photoPath: string };
  };
  /** Free-text note per item key. Display/recordkeeping only — a note never
   *  marks an item done (done-ness is structural; see checklistItemDone). */
  notes: Record<string, string>;
  /** Which items have their note editor expanded (UI only). */
  noteOpen: Record<string, boolean>;
  /** Which "why do we need this" info panels are expanded (UI only). */
  info: Record<string, boolean>;
  /** Admin-only: items the reviewer chose to skip for now (UI ordering only). */
  skipped: Record<string, boolean>;
  /** Which groups are collapsed (UI only). */
  groups: Record<string, boolean>;
};

const emptyPerson = (): Person => ({ name: "", phone: "", email: "" });

export function newChecklist(): ChecklistState {
  return {
    docs: DOC_IDS.reduce(
      (acc, id) => {
        acc[id] = { status: "open", src: null, note: null };
        return acc;
      },
      {} as Record<DocId, DocState>
    ),
    rbNumber: "",
    smoke: null,
    key: null,
    wlanUser: "",
    wlanPass: "",
    wlanPassProvided: false,
    people: {
      hv: emptyPerson(),
      hm: emptyPerson(),
      key: emptyPerson(),
      view: emptyPerson(),
    },
    viewing: { allowed: null },
    shots: {},
    pics: {},
    picPaths: {},
    videos: {},
    equip: {
      ware: { ok: null, photo: false, photoPath: "" },
      washer: { ok: null, photo: false, photoPath: "" },
    },
    notes: {},
    noteOpen: {},
    info: {},
    skipped: {},
    groups: {},
  };
}

export type ChecklistProgress = {
  /** Done out of 15. */
  done: number;
  total: number;
  /** Per-group tallies (for the Prüfung summary grid). */
  docsDone: number;
  objectDone: number;
  contactsDone: number;
  /** Smoke answered "no" — owner cannot submit until resolved. */
  blocked: boolean;
};

const hasPersonContact = (p: Person): boolean =>
  !!(p.name.trim() && p.phone.trim());

/**
 * Whether each of the 15 proofs is done. The single source of truth for "done"
 * — both the x/15 tally (computeChecklistProgress) and the persisted status
 * (checklistToRows maps done → "fulfilled") read it, so the stored
 * apartment_checklist_items can never disagree with what the wizard shows.
 *
 * Done-ness comes from structured state ONLY — a free-text note never promotes
 * an item. (A note is display/recordkeeping; the old "note-covers rule" let a
 * typed note fake a proof — e.g. smoke done with the yes/no unanswered, or a
 * stale note count an emptied proof as done on resubmit — so it was dropped.)
 */
export function checklistItemDone(
  s: ChecklistState,
  opts: { bedrooms: number; keynestAvailable?: boolean }
): Record<ChecklistItemKey, boolean> {
  const { bedrooms, keynestAvailable } = opts;

  // Photos: every slot of every shot key filled.
  const keys = shotKeys(bedrooms);
  const shotsOk = keys.every((k) =>
    Array.from({ length: photoCap(k) }, (_, i) => !!s.pics[`${k}:${i}`]).every(
      Boolean
    )
  );
  // KeyNest off (prop-driven) forces the person path.
  const keyEff = keynestAvailable !== false ? s.key : "person";
  const v = s.viewing;

  const done = {} as Record<ChecklistItemKey, boolean>;
  // Documents (7): status "done" (gas N/A also carries status "done").
  for (const id of DOC_IDS) done[id] = s.docs[id].status === "done";
  // Rundfunk is done only on a real proof — a typed contribution number, or an
  // uploaded application confirmation. Picking a mode button must NOT count it
  // (a stale "Beantragt" note with no file therefore reopens as incomplete).
  done.rundfunk =
    (s.docs.rundfunk.rb === "number" && !!s.rbNumber.trim()) ||
    (s.docs.rundfunk.rb === "applied" && !!s.docs.rundfunk.fileUrl);
  done.photos = shotsOk;
  // Inventory + washing machine: ok===true AND a photo.
  done.ware = s.equip.ware.ok === true && s.equip.ware.photo;
  done.washer = s.equip.washer.ok === true && s.equip.washer.photo;
  // Smoke detectors are legally required: done ONLY on an explicit "yes".
  done.smoke = s.smoke === "yes";
  done.key =
    keyEff === "keynest" ||
    (keyEff === "person" && hasPersonContact(s.people.key));
  // The doorbell nameplate is always considered done (free-text, defaulted).
  done.bell = true;
  done.contact = hasPersonContact(s.people.hv) || hasPersonContact(s.people.hm);
  done.viewing =
    v.allowed === false ||
    (v.allowed === true && hasPersonContact(s.people.view));
  return done;
}

/**
 * Count the 15 proofs that are done.
 *
 * `mode` does not affect the tally (it only changes display ordering and the
 * "requested" chip in the UI), so it isn't a parameter here.
 */
export function computeChecklistProgress(
  s: ChecklistState,
  opts: { bedrooms: number; keynestAvailable?: boolean }
): ChecklistProgress {
  const done = checklistItemDone(s, opts);
  const docsDone = DOC_IDS.filter((id) => done[id]).length;
  const objectDone = (
    ["photos", "ware", "washer", "smoke", "key", "bell"] as const
  ).filter((k) => done[k]).length;
  const contactsDone = (["contact", "viewing"] as const).filter(
    (k) => done[k]
  ).length;

  return {
    done: docsDone + objectDone + contactsDone,
    total: 15,
    docsDone,
    objectDone,
    contactsDone,
    blocked: s.smoke === "no",
  };
}

/** Per-item status for the Prüfung checklist card: done (teal) / requested
 *  (amber) / open (red). "requested" only applies to the 7 document proofs an
 *  admin flagged "request from owner"; everything else is just done or open. */
export type ChecklistItemReviewStatus = "done" | "requested" | "open";

export function checklistItemStatuses(
  s: ChecklistState,
  opts: { bedrooms: number; keynestAvailable?: boolean }
): Record<ChecklistItemKey, ChecklistItemReviewStatus> {
  const done = checklistItemDone(s, opts);
  const out = {} as Record<ChecklistItemKey, ChecklistItemReviewStatus>;
  for (const key of CHECKLIST_ITEM_KEYS) {
    if (done[key]) out[key] = "done";
    else if (
      (DOC_IDS as readonly string[]).includes(key) &&
      s.docs[key as DocId].status === "requested"
    )
      out[key] = "requested";
    else out[key] = "open";
  }
  return out;
}

/** Item labels for the Prüfung checklist card, in CHECKLIST_ITEM_KEYS order. */
export const CHECKLIST_ITEM_LABELS: {
  key: ChecklistItemKey;
  i18nKey: string;
  fallback: string;
}[] = [
  { key: "wlan", i18nKey: "admin.checklist.item.wlan", fallback: "Wi-Fi" },
  {
    key: "strom",
    i18nKey: "admin.checklist.item.strom",
    fallback: "Electricity contract",
  },
  {
    key: "gas",
    i18nKey: "admin.checklist.item.gas",
    fallback: "Gas or heating contract",
  },
  {
    key: "rundfunk",
    i18nKey: "admin.checklist.item.rundfunk",
    fallback: "Broadcasting fee",
  },
  {
    key: "energie",
    i18nKey: "admin.checklist.item.energie",
    fallback: "Energy certificate",
  },
  {
    key: "eigentum",
    i18nKey: "admin.checklist.item.eigentum",
    fallback: "Proof of ownership",
  },
  {
    key: "grundriss",
    i18nKey: "admin.checklist.item.grundriss",
    fallback: "Floor plan",
  },
  { key: "photos", i18nKey: "admin.checklist.item.photos", fallback: "Photos" },
  {
    key: "ware",
    i18nKey: "admin.checklist.item.ware",
    fallback: "Cutlery & crockery",
  },
  {
    key: "washer",
    i18nKey: "admin.checklist.item.washer",
    fallback: "Washing machine",
  },
  {
    key: "smoke",
    i18nKey: "admin.checklist.item.smoke",
    fallback: "Smoke detectors",
  },
  {
    key: "key",
    i18nKey: "admin.checklist.item.key",
    fallback: "Keys & handover",
  },
  {
    key: "bell",
    i18nKey: "admin.checklist.item.bell",
    fallback: "Doorbell name",
  },
  {
    key: "contact",
    i18nKey: "admin.checklist.item.contact",
    fallback: "Management & caretaker",
  },
  {
    key: "viewing",
    i18nKey: "admin.checklist.item.viewing",
    fallback: "First viewing",
  },
];

/**
 * Map a ChecklistState to the apartment_checklist_items rows to persist (one per
 * item_key). Shared by both submit paths — the staff hook inserts these rows
 * directly; the owner hook ships them in the RPC payload.
 *
 * Status mirrors the x/15 tally: a done item is stored "fulfilled" so the DB's
 * apartment_checklist_progress() agrees with the wizard. Gas marked N/A →
 * "not_applicable"; a document still requested (not yet done) → "requested";
 * everything else → "open".
 *
 * Structured extras that have no column fold into `note` (see the header): the
 * WLAN network name + a "password provided" marker — NEVER the password itself —
 * the Rundfunk mode/number, the key-handover mode, and the contact people.
 *
 * Not persisted here: apartment_checklist_files (the wizard tracks uploads as
 * in-memory booleans, no URLs) and apartments.doorbell_name (not captured in
 * ChecklistState) — both deferred until the UI collects them.
 */
export function checklistToRows(
  s: ChecklistState,
  opts: { bedrooms: number; keynestAvailable?: boolean }
): ChecklistItemRow[] {
  const done = checklistItemDone(s, opts);
  const join = (...parts: (string | null | undefined)[]): string | null => {
    const txt = parts
      .map((p) => (p ?? "").trim())
      .filter(Boolean)
      .join(" — ");
    return txt || null;
  };
  // "HV: name, phone" — plus ", email" only when one was given, so a
  // contact with no email round-trips byte-identically (the email group in
  // takeFoldedExtras is optional). name/phone carry no comma, so the commas
  // stay unambiguous separators.
  const contactLine = (tag: string, p: Person): string =>
    `${tag}: ${p.name}, ${p.phone}${p.email.trim() ? `, ${p.email.trim()}` : ""}`;

  const docStatus = (id: DocId): ChecklistItemStatus => {
    // N/A wins over "done": gas marked not-applicable also carries status
    // "done" (so the x/15 tally counts it), but it must persist as
    // not_applicable, so this check comes before the done→fulfilled line.
    if (id === "gas" && s.docs.gas.na) return "not_applicable";
    if (done[id]) return "fulfilled";
    if (s.docs[id].status === "requested") return "requested";
    return "open";
  };

  const extras: Partial<Record<ChecklistItemKey, string | null>> = {
    wlan: join(
      s.wlanUser ? `Netzwerk: ${s.wlanUser}` : null,
      s.wlanPass.trim() || s.wlanPassProvided ? "Passwort hinterlegt" : null
    ),
    rundfunk: join(
      s.docs.rundfunk.rb === "number" && s.rbNumber
        ? `Beitragsnummer: ${s.rbNumber}`
        : s.docs.rundfunk.rb === "applied"
          ? "Beantragt"
          : s.docs.rundfunk.rb === "notyet"
            ? "Noch nicht angemeldet"
            : null
    ),
    key: join(
      s.key === "keynest"
        ? "Übergabe: KeyNest"
        : s.key === "person"
          ? `Übergabe: Person — ${s.people.key.name} ${s.people.key.phone}`.trim()
          : null
    ),
    contact: join(
      hasPersonContact(s.people.hv) ? contactLine("HV", s.people.hv) : null,
      hasPersonContact(s.people.hm) ? contactLine("HM", s.people.hm) : null
    ),
    viewing: join(
      s.viewing.allowed === false
        ? "Besichtigung: nicht erlaubt"
        : s.viewing.allowed === true
          ? `Besichtigung: erlaubt — ${s.people.view.name} ${s.people.view.phone}`.trim()
          : null
    ),
  };

  return CHECKLIST_ITEM_KEYS.map((key) => {
    const isDoc = (DOC_IDS as readonly string[]).includes(key);
    const status: ChecklistItemStatus = isDoc
      ? docStatus(key as DocId)
      : done[key]
        ? "fulfilled"
        : "open";
    const source: DocSource = isDoc ? s.docs[key as DocId].src : null;
    const note = join(
      isDoc ? s.docs[key as DocId].note : null,
      s.notes[key],
      extras[key],
      isDoc && s.docs[key as DocId].fileUrl
        ? `Datei: ${s.docs[key as DocId].fileUrl}`
        : null
    );
    return { item_key: key, status, source, note };
  });
}

/**
 * Inverse of the `extras` folding in checklistToRows: pull the structured
 * sub-fields back out of a stored `note` and into their editable inputs, and
 * return the residual free-text (the owner's own note, minus every folded
 * fragment). This is what makes resubmit non-destructive and done-ness purely
 * structural:
 *   - Docs (wlan / rundfunk) come back done via their stored status, so their
 *     fragments are stripped from the note. The wlan network name IS restored,
 *     and a "password provided" boolean (never the plaintext — none survives the
 *     session) so the marker isn't silently lost each round-trip. Rundfunk's mode
 *     + number ARE restored, so a re-save regenerates them instead of dropping
 *     them.
 *   - key / contact / viewing have no status/boolean backing, so their people
 *     MUST be restored here — that reconstruction is the only thing that keeps
 *     them done on reload now that a note never promotes an item.
 * Stripping each fragment (rather than carrying the whole note back) also kills
 * the old pile-up: a re-save folds the restored fields in exactly once.
 *
 * ponytail: the name/phone split for the space-joined key-person and viewing
 * templates is a heuristic — the trailing token is taken as a phone only when it
 * is phone-shaped, else the whole remainder is the name (so a two-word name with
 * no phone no longer mis-splits). name + " " + phone re-joins to the original
 * string, so the re-saved note is byte-identical. Upgrade path: persist people /
 * network name / rb number as columns and hydrate those.
 */
function takeFoldedExtras(
  key: string,
  note: string,
  s: ChecklistState
): string {
  let rest = note;
  const strip = (re: RegExp, on?: (m: RegExpMatchArray) => void): void => {
    const m = rest.match(re);
    if (!m || m.index == null) return;
    on?.(m);
    rest = rest.slice(0, m.index) + rest.slice(m.index + m[0].length);
  };
  const setPerson = (p: Person, name: string, phone: string, email = "") => {
    p.name = name.trim();
    p.phone = phone.trim();
    p.email = email.trim();
  };

  // Any document can carry an uploaded-proof URL, folded in as `Datei: <url>`.
  // A URL has no spaces, so stop at the first whitespace / separator.
  if ((DOC_IDS as readonly string[]).includes(key)) {
    strip(/Datei: (\S+)/, (m) => {
      s.docs[key as DocId].fileUrl = m[1];
    });
  }

  if (key === "wlan") {
    strip(/Netzwerk: ([^—]*)/, (m) => {
      s.wlanUser = m[1].trim();
    });
    // ponytail: restore the "provided" marker, never the plaintext password —
    // no encrypted column survives the session, so the raw value stays dropped.
    strip(/Passwort hinterlegt/, () => {
      s.wlanPassProvided = true;
    });
  } else if (key === "rundfunk") {
    strip(/Beitragsnummer: ([^—]*)/, (m) => {
      s.docs.rundfunk.rb = "number";
      s.rbNumber = m[1].trim();
    });
    strip(/Beantragt/, () => (s.docs.rundfunk.rb = "applied"));
    strip(/Noch nicht angemeldet/, () => (s.docs.rundfunk.rb = "notyet"));
  } else if (key === "key") {
    strip(/Übergabe: Person — ([^—]+?)(?:\s(\+?[\d][\d\s()/-]*))?$/, (m) => {
      s.key = "person";
      setPerson(s.people.key, m[1], m[2] ?? "");
    });
    strip(/Übergabe: KeyNest/, () => (s.key = "keynest"));
  } else if (key === "contact") {
    // Optional trailing ", email" — phone stops at the comma, so an email
    // (when present) peels off as the third field; absent, the group simply
    // doesn't match and email stays empty.
    strip(/HV: ([^,—]*), ([^,—]*)(?:, ([^—]*))?/, (m) =>
      setPerson(s.people.hv, m[1], m[2], m[3] ?? "")
    );
    strip(/HM: ([^,—]*), ([^,—]*)(?:, ([^—]*))?/, (m) =>
      setPerson(s.people.hm, m[1], m[2], m[3] ?? "")
    );
  } else if (key === "viewing") {
    strip(
      /Besichtigung: erlaubt — ([^—]+?)(?:\s(\+?[\d][\d\s()/-]*))?$/,
      (m) => {
        s.viewing.allowed = true;
        setPerson(s.people.view, m[1], m[2] ?? "");
      }
    );
    strip(/Besichtigung: nicht erlaubt/, () => (s.viewing.allowed = false));
  }

  // Tidy separators left where a fragment was removed.
  return rest
    .replace(/\s*—\s*—\s*/g, " — ")
    .replace(/^\s*—\s*/, "")
    .replace(/\s*—\s*$/, "")
    .trim();
}

/**
 * Rebuild a ChecklistState from stored apartment_checklist_items rows — the
 * inverse of checklistToRows, used to hydrate the wizard when an owner edits an
 * already-submitted property (owner_get_submission_for_edit returns the rows).
 * Without this the edit read-back returned newChecklist() (empty), so
 * owner_resubmit_property re-inserted from an empty payload and silently wiped
 * every stored proof on any field edit.
 *
 * Done-ness is reconstructed STRUCTURALLY (a note never promotes an item):
 *   - docs: the stored status (docs[id].status = "done"; gas N/A → done + na),
 *   - photos / inventory / smoke: the done-making booleans (their stored note is
 *     empty when completed structurally),
 *   - key / contact / viewing: the people parsed back by takeFoldedExtras.
 * ALL notes are preserved (every status, not just fulfilled) with their folded
 * fragments stripped, so a "fix typo" resubmit loses nothing and a stale note on
 * an open/requested row can no longer resurrect it as done.
 *
 * ponytail: structured round-trip, not file fidelity. photos/inventory come back
 * as "all slots filled" booleans with no file URLs; the WLAN network name and a
 * person's name/phone split are display heuristics (see takeFoldedExtras).
 * Upgrade path: return apartment_checklist_files + structured columns and hydrate
 * them here.
 */
export function rowsToChecklist(
  rows: ReadonlyArray<{
    item_key: string;
    status: string;
    source?: string | null;
    note?: string | null;
  }>,
  opts: { bedrooms: number }
): ChecklistState {
  const s = newChecklist();
  const byKey = new Map(rows.map((r) => [r.item_key, r]));

  for (const id of DOC_IDS) {
    const row = byKey.get(id);
    if (!row) continue;
    if (row.source === "owner" || row.source === "coliviq")
      s.docs[id].src = row.source;
    if (id === "gas" && row.status === "not_applicable") {
      s.docs.gas.status = "done";
      s.docs.gas.na = true;
    } else if (row.status === "fulfilled") {
      s.docs[id].status = "done";
    } else if (row.status === "requested") {
      s.docs[id].status = "requested";
    }
  }

  const fulfilled = (k: ChecklistItemKey) =>
    byKey.get(k)?.status === "fulfilled";

  // Boolean-backed proofs: reconstruct what makes them done, since their stored
  // note is empty when the owner completed them structurally.
  if (fulfilled("photos")) {
    for (const k of shotKeys(opts.bedrooms))
      for (let i = 0; i < photoCap(k); i++) s.pics[`${k}:${i}`] = true;
  }
  if (fulfilled("ware"))
    s.equip.ware = { ok: true, photo: true, photoPath: "" };
  if (fulfilled("washer"))
    s.equip.washer = { ok: true, photo: true, photoPath: "" };
  if (fulfilled("smoke")) s.smoke = "yes";

  // Preserve every note (any status), but parse its folded structured fragments
  // back into their inputs first — so key/contact/viewing recompute done, docs
  // stay done via status, and a re-save folds each field in exactly once (no
  // pile-up). What's left is the owner's own free text. A note on an
  // open/requested row is kept but no longer makes the item done.
  for (const r of rows) {
    const kept = takeFoldedExtras(r.item_key, r.note ?? "", s);
    if (kept) s.notes[r.item_key] = kept;
  }

  return s;
}
